import React, { useContext, useMemo, useState } from 'react';
import PropTypes, { oneOfType } from 'prop-types';
import { Link } from 'react-router-dom';
import format from 'date-fns/format';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import isValid from 'date-fns/isValid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faExclamation,
  faExclamationCircle,
  faExclamationTriangle,
  faSquare,
} from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';
import { faBell as faBellOutline } from '@fortawesome/free-regular-svg-icons';
import ReactTooltip from 'react-tooltip';

import {
  ALERTS_CLEAR,
  ASSET_MODEL,
  ASSET_MODEL_READ,
  CORE,
  CORE_READ,
} from '../../Constants/RBAC';
import OrgsMapContext from '../../Context/OrgsMapContext';
import DM1FaultsConstants from '../../Lib/DM1FaultsConstants';
import j1939Dicitionary from '../../Static/j1939Dictionary.json';
import MobileDetailsModal from './MobileDetailsModal';
import Can from '../../RBAC/Can';

const { className: tooltipClass, styles: tooltipStyles } = css.resolve`
  div {
    z-index: 1021;
  }
`;

const DM_CONSTANTS = new DM1FaultsConstants();
const ICON_DISPLAY_MAP = new Map([
  ['alerts:positive', { icon: faBell, color: 'alert-orange' }],
  ['alerts:negative', { icon: faBellOutline, color: 'icon-link-gray' }],
  ['dm1:positive', { icon: faExclamationTriangle, color: 'active-dm' }],
  ['dm1:negative', { icon: faExclamationTriangle, color: 'icon-link-gray' }],
]);
const LAMPS_ICON_MAP = new Map([
  ['AWL', SquareIcon],
  ['MIL:PL', TriangleIcon],
  ['RSL', CircleIcon],
]);

function getDm1DisplayName(SPN, FMI) {
  const spnName = j1939Dicitionary[SPN] ? j1939Dicitionary[SPN].SPN_Name : SPN;
  return `${spnName} - ${DM_CONSTANTS.listFMIname[FMI]}`;
}

export default function AlertCard({
  alert,
  tab,
  assetView,
  engineAppView,
  handleRuleLinkClick,
  toggleClearAlertModalClick,
}) {
  const {
    AWL,
    DetectedAt,
    FMI,
    GatewayId,
    GatewayName,
    GpsData: { Lat, Long } = {},
    Id,
    MIL,
    OrgId,
    PL,
    RSL,
    RuleId,
    RuleName,
    SPN,
    Transition,
    TriggeredAt,
  } = alert;

  const orgsMap = useContext(OrgsMapContext);

  const [showModal, toggleModal] = useState(false);
  const { icon, color } = ICON_DISPLAY_MAP.get(`${tab}:${Transition}`) || {};
  const border =
    Transition === 'positive' ? `border border-${color}` : 'border-0';
  const title =
    tab === 'alerts' ? (
      <Can
        no={() => (
          <button
            className="btn btn-link p-0 text-decoration-none"
            disabled
            onClick={() => null}
          >
            <span className={`text-${color}`}>{RuleName}</span>
          </button>
        )}
        orgID={OrgId}
        perform={ASSET_MODEL_READ}
        resource={ASSET_MODEL}
        yes={() => (
          <button
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => handleRuleLinkClick(GatewayId, RuleId)}
          >
            <span className={`text-${color}`}>{RuleName}</span>
          </button>
        )}
      />
    ) : (
      getDm1DisplayName(SPN, FMI)
    );

  const displayTime = useMemo(() => {
    switch (tab) {
      case 'alerts': {
        const detectedAtDate = new Date(DetectedAt);
        return isValid(detectedAtDate)
          ? format(detectedAtDate, 'MMM d Y - h:mmaaa')
          : null;
      }
      case 'dm1': {
        const triggeredAtDate = new Date(TriggeredAt);
        return isValid(triggeredAtDate)
          ? format(triggeredAtDate, 'MMM d Y - h:mmaaa')
          : null;
      }
      default:
        return null;
    }
  }, [DetectedAt, tab, TriggeredAt]);

  const durationDisplay = useMemo(() => {
    switch (tab) {
      case 'alerts': {
        const detectedAtDate = new Date(DetectedAt);
        return isValid(detectedAtDate) && Transition === 'positive'
          ? `(${formatDistanceToNow(detectedAtDate, { includeSeconds: true })})`
          : null;
      }
      case 'dm1': {
        const triggeredAtDate = new Date(TriggeredAt);
        return isValid(triggeredAtDate) && Transition === 'positive'
          ? `(${formatDistanceToNow(triggeredAtDate, {
              includeSeconds: true,
            })})`
          : null;
      }
      default:
        return null;
    }
  }, [DetectedAt, tab, Transition, TriggeredAt]);

  return (
    <div
      className={`card mb-3 d-lg-none ${border}${
        assetView || engineAppView ? '' : ' row'
      }`}
    >
      <div className="row no-gutters">
        <div className="col-2 d-flex flex-column align-items-center justify-content-center">
          {tab === 'alerts' ? (
            <FontAwesomeIcon
              icon={icon}
              className={`text-${color}`}
              size="lg"
            />
          ) : (
            [
              [[AWL], 'AWL'],
              [[MIL, PL], 'MIL:PL'],
              [[RSL], 'RSL'],
            ].map(([lamps, iconKey], i) => {
              if (!lamps.includes(1)) return null;

              let tooltip;
              if (lamps.length === 1) {
                tooltip = `Active ${iconKey}`;
              } else {
                const lampNames = iconKey.split(':');
                tooltip = lamps.reduce((str, lamp, index) => {
                  if (lamp !== 0) return str;
                  return `${str} ${lampNames[index]}`;
                }, 'Active');
              }
              const Icon = LAMPS_ICON_MAP.get(iconKey);
              return (
                <div
                  data-for={`${Id}-${i}`}
                  data-tip={tooltip}
                  key={`${Id}-${i}`}
                >
                  <Icon />
                  <ReactTooltip
                    arrowColor="#fff"
                    className="bg-white shadow rounded-lg text-dark three-quarter-text"
                    id={`${Id}-${i}`}
                    multiline
                  />
                </div>
              );
            })
          )}
        </div>
        <div className="col-10">
          <div className="card-body py-3">
            <div className={`card-title mb-0 text-${color} text-truncate`}>
              {title}
            </div>
            <p className="card-text text-text-light-gray three-quarter-text mb-2">
              {displayTime}
            </p>
            <p className="card-text text-text-light-gray three-quarter-text mb-2">
              {durationDisplay}
            </p>
            {assetView ? null : (
              <p className="card-text text-text-light-gray three-quarter-text mb-2">
                on
                <span className="ml-1">
                  <Can
                    no={() => (
                      <button
                        className="btn btn-link p-0 nine-tenth-text"
                        disabled
                      >
                        {GatewayName}
                      </button>
                    )}
                    orgID={OrgId}
                    perform={CORE_READ}
                    resource={CORE}
                    yes={() => (
                      <Link
                        to={{
                          pathname: `/asset/${GatewayId}`,
                          search: '?tab=dashboard',
                        }}
                        replace={true}
                        className="nine-tenth-text"
                      >
                        {GatewayName}
                      </Link>
                    )}
                  />
                </span>
              </p>
            )}
            {assetView ? null : (
              <p className="card-text text-text-light-gray three-quarter-text mb-3">
                in
                <span className="ml-2 nine-tenth-text text-dark">
                  {orgsMap?.[OrgId]?.name}
                </span>
              </p>
            )}
            <div
              className={`d-flex align-items-center ${getContentClassName(
                tab,
                Transition
              )}`}
            >
              {tab === 'alerts' && Transition === 'positive' ? (
                <Can
                  no={(data) => (
                    <div data-tip={data}>
                      <button
                        className="btn btn-outline-text-light-gray py-1 px-3 rounded-pill"
                        disabled
                      >
                        Clear
                      </button>
                      <ReactTooltip className={tooltipClass} />
                      {tooltipStyles}
                    </div>
                  )}
                  orgID={OrgId}
                  perform={ALERTS_CLEAR}
                  resource={CORE}
                  yes={() => (
                    <button
                      className="btn btn-outline-text-light-gray py-1 px-3 rounded-pill"
                      onClick={() => toggleClearAlertModalClick(alert)}
                    >
                      Clear
                    </button>
                  )}
                />
              ) : null}
              <button
                className="btn btn-outline-text-light-gray py-1 px-3 rounded-pill mx-1"
                onClick={() => toggleModal(!showModal)}
              >
                Details
              </button>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${Lat},${Long}`}
                className={` ${tab === 'dm1' ? 'd-none' : ''}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="btn btn-outline-text-light-gray py-1 px-3 rounded-pill mx-1">
                  Location
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
      <MobileDetailsModal
        alert={alert}
        assetView={assetView}
        handleRuleLinkClick={handleRuleLinkClick}
        isOpen={showModal}
        tab={tab}
        toggleModal={toggleModal}
      />
      <style jsx>{`
        .card {
          border-width: medium !important;
        }
        .btn-outline-text-light-gray {
          font-size: 0.65rem;
        }
      `}</style>
    </div>
  );
}

AlertCard.propTypes = {
  alert: PropTypes.shape({
    AlertData: PropTypes.shape({
      description: oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      signals: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          signalID: PropTypes.string,
          threshold: oneOfType([PropTypes.number, PropTypes.string]),
          type: PropTypes.string,
          value: PropTypes.number,
        })
      ),
    }),
    AWL: PropTypes.number,
    Canbus: PropTypes.number,
    CreatedAt: PropTypes.string,
    DetectedAt: PropTypes.string,
    FMI: PropTypes.number,
    GatewayId: PropTypes.string,
    GatewayName: PropTypes.string,
    GpsData: PropTypes.shape({
      Lat: PropTypes.number,
      Long: PropTypes.number,
    }),
    Id: PropTypes.string,
    MIL: PropTypes.number,
    OC: PropTypes.number,
    OrgId: PropTypes.string,
    PL: PropTypes.number,
    RSL: PropTypes.number,
    RuleId: PropTypes.string,
    RuleName: PropTypes.string,
    Severity: PropTypes.string,
    SourceAddress: PropTypes.number,
    SPN: PropTypes.number,
    Transition: PropTypes.oneOf(['positive', 'negative']),
    TriggeredAt: PropTypes.string,
    Type: PropTypes.oneOf(['rule']),
    UpdatedAt: PropTypes.string,
  }),
  assetView: PropTypes.bool,
  engineAppView: PropTypes.bool,
  handleRuleLinkClick: PropTypes.func,
  tab: PropTypes.oneOf(['alerts', 'dm1']),
  toggleClearAlertModalClick: PropTypes.func,
};

AlertCard.defaultProps = { alert: { Transition: 'positive' }, tab: 'alerts' };

function getContentClassName(tab, Transition) {
  switch (true) {
    case tab === 'dm1':
      return 'justify-content-end';
    case tab === 'alerts' && Transition === 'positive':
      return 'justify-content-around';
    default:
      return 'justify-content-start';
  }
}

function CircleIcon() {
  return (
    <FontAwesomeIcon
      className="text-active-dm my-1"
      icon={faExclamationCircle}
      size="lg"
    />
  );
}

function SquareIcon() {
  return (
    <span className="fa-layers fa-fw my-1">
      <FontAwesomeIcon
        className="text-alert-orange"
        icon={faSquare}
        size="lg"
      />
      <FontAwesomeIcon
        color="white"
        icon={faExclamation}
        transform="shrink-6"
      />
    </span>
  );
}

function TriangleIcon() {
  return (
    <FontAwesomeIcon
      className="text-warning-yellow my-1"
      icon={faExclamationTriangle}
      size="lg"
    />
  );
}
